import React, { JSX } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import Layout from 'components/Layout'
import { QUICK_OVERLAYS_PATH } from 'lib/paths'
import { EventDetailLayout } from 'components/EventDetailLayout'
import { EventsPage } from 'pages/EventsPage'
import {
  QuickOverlaysPage,
  QuickOverlaysChatEditorPage,
  QuickOverlaysEmoteWallEditorPage,
  QuickOverlaysMerchEditorPage
} from 'pages/QuickOverlaysPage'
import { ConnectionsPage } from 'pages/ConnectionsPage'
import { SupportPage } from 'pages/SupportPage'
import { AssetsPage } from 'pages/AssetsPage'
import { BotsPage } from 'pages/BotsPage'
import { AllStreamsPage } from 'pages/AllStreamsPage'
import { UsagePage } from 'pages/UsagePage'
import { AccountPage } from 'pages/AccountPage'
import { BillingPage } from 'pages/BillingPage'
import { OverviewPage } from 'pages/event/OverviewPage'
import { IntegrationsPage } from 'pages/event/IntegrationsPage'
import { OverlayEditorPage } from 'pages/event/OverlayEditorPage'
import { ChatsPage } from 'pages/event/ChatsPage'
import { EmoteWallsPage } from 'pages/event/EmoteWallsPage'
import { CameraControlsPage } from 'pages/event/CameraControlsPage'
import { GiveawaysPage } from 'pages/event/GiveawaysPage'
import { PollsPage } from 'pages/event/PollsPage'
import { TriviaPage } from 'pages/event/TriviaPage'
import { ScoreboardPage } from 'pages/event/ScoreboardPage'
import { TugsPage } from 'pages/event/TugsPage'
import { AccessPage } from 'pages/event/AccessPage'
import { ShopifyConnectedPage } from 'pages/ShopifyConnectedPage'
import { ChatOverlayPage } from 'pages/overlay/ChatOverlayPage'
import { EmoteWallOverlayPage } from 'pages/overlay/EmoteWallOverlayPage'
import { MerchOverlayPage } from 'pages/overlay/MerchOverlayPage'
import { KeyOverlayPage } from 'pages/overlay/KeyOverlayPage'
import { CompositeOverlayPage } from 'pages/overlay/CompositeOverlayPage'
import { CamControlOverlayPage } from 'pages/overlay/CamControlOverlayPage'
import { withAuthenticationRequired } from '@auth0/auth0-react'
import { EventEntitlementGate } from 'components/EntitlementGate'

const ProtectedLayout = withAuthenticationRequired(Layout)

const gated = (element: JSX.Element): JSX.Element => (
  <EventEntitlementGate feature='interactiveContent'>{element}</EventEntitlementGate>
)

const Routing = (): JSX.Element => {
  return (
    <Routes>
      <Route path='o/chat/:chatId' element={<ChatOverlayPage />} />
      <Route path='o/emotes/:emoteWallId' element={<EmoteWallOverlayPage />} />
      <Route path='o/merch/:merchOverlayId' element={<MerchOverlayPage />} />
      <Route path='k/:k' element={<KeyOverlayPage />} />
      <Route path='overlay/:slug/composite' element={<CompositeOverlayPage />} />
      <Route path='overlay/:slug/composite-mobile' element={<CompositeOverlayPage kind='MOBILE' />} />
      <Route path='overlay/:slug/camctl/:camControlId' element={<CamControlOverlayPage />} />
      <Route path='shopify/connected' element={<ShopifyConnectedPage />} />

      <Route
        path='/'
        element={<ProtectedLayout />}
      >
        <Route index element={<Navigate to={QUICK_OVERLAYS_PATH} replace />} />
        <Route path='overlays' element={<QuickOverlaysPage />} />
        <Route path='support' element={<SupportPage />} />
        <Route path='overlays/chats' element={<Navigate to={QUICK_OVERLAYS_PATH} replace />} />
        <Route path='overlays/chats/new' element={<QuickOverlaysChatEditorPage />} />
        <Route path='overlays/chats/:chatId' element={<QuickOverlaysChatEditorPage />} />
        <Route path='overlays/emote-walls' element={<Navigate to={QUICK_OVERLAYS_PATH} replace />} />
        <Route path='overlays/emote-walls/new' element={<QuickOverlaysEmoteWallEditorPage />} />
        <Route path='overlays/emote-walls/:emoteWallId' element={<QuickOverlaysEmoteWallEditorPage />} />
        <Route path='overlays/merch' element={<Navigate to={QUICK_OVERLAYS_PATH} replace />} />
        <Route path='overlays/merch/new' element={<QuickOverlaysMerchEditorPage />} />
        <Route path='overlays/merch/:merchOverlayId' element={<QuickOverlaysMerchEditorPage />} />
        <Route path='connections' element={<ConnectionsPage />} />
        <Route path='assets' element={<AssetsPage />} />
        <Route path='streams' element={<EventsPage />} />
        <Route path='events/new' element={<EventsPage />} />
        <Route path='events/:eventId' element={<EventDetailLayout />}>
          <Route index element={<Navigate to='overview' replace />} />
          <Route path='overview' element={<OverviewPage />} />
          <Route path='integrations' element={gated(<IntegrationsPage />)} />
          <Route path='polls' element={gated(<PollsPage />)} />
          <Route path='trivia' element={gated(<TriviaPage />)} />
          <Route path='scoreboard' element={gated(<ScoreboardPage />)} />
          <Route path='tug-of-wars' element={gated(<TugsPage />)} />
          <Route path='desktop-overlay' element={gated(<OverlayEditorPage kind='DESKTOP' />)} />
          <Route path='mobile-overlay' element={gated(<OverlayEditorPage kind='MOBILE' />)} />
          <Route path='chats' element={<ChatsPage />} />
          <Route path='chats/new' element={<ChatsPage />} />
          <Route path='chats/:chatId' element={<ChatsPage />} />
          <Route path='emote-walls' element={<EmoteWallsPage />} />
          <Route path='emote-walls/new' element={<EmoteWallsPage />} />
          <Route path='emote-walls/:emoteWallId' element={<EmoteWallsPage />} />
          <Route path='camera-controls' element={<CameraControlsPage />} />
          <Route path='giveaways' element={<GiveawaysPage />} />
          <Route path='access' element={<AccessPage />} />
        </Route>

        <Route path='admin' element={<BotsPage />} />
        <Route path='admin/streams' element={<AllStreamsPage />} />
        <Route path='bots' element={<Navigate to='/admin' replace />} />
        <Route path='usage' element={<UsagePage />} />
        <Route path='settings' element={<Navigate to='/settings/account' replace />} />
        <Route path='settings/account' element={<AccountPage />} />
        <Route path='billing' element={<BillingPage />} />
      </Route>
    </Routes>
  )
}

export default Routing
